import { RouteRow } from './routeModel';

/**
 * Shared filter state applied consistently across topology, table, and metrics.
 * Direction lives outside this model (controlled by the existing DirectionTabs);
 * best-path-only lives on the existing BestOnlyToggle control but is applied through
 * the same `applyRouteFilters` pass so all three surfaces stay in sync.
 */
export interface RouteFilters {
  /** General text search — matched against prefix, neighbor IP, AS-path, next hop and reflector. */
  search: string;
  /** Prefix/CIDR text match — supports exact and partial (substring) matching. */
  prefix: string;
  neighborIp: string;
  /** Numeric ASN as free text; ignored when not a valid number. */
  neighborAs: string;
  /** Reflector subId to narrow to a single cluster member; null = all members. */
  reflectorId: string | null;
  /** Numeric Local Preference as free text; ignored when not a valid number. */
  localPref: string;
  /**
   * AWS tag that discovered the destination (CTX-129).
   *
   * Three states, following `reflectorId`'s null-for-inactive convention:
   *   `null` — not filtering on tags
   *   `''`   — any tag-discovered destination, regardless of which tag
   *   text   — a tag key (`RT`) or an exact pair (`RT=100`), matched case-insensitively
   *            and partially on both sides
   */
  tag: string | null;
  family: 'all' | 'ipv4-unicast' | 'vpnv4';
}

export const DEFAULT_ROUTE_FILTERS: RouteFilters = {
  search: '',
  prefix: '',
  neighborIp: '',
  neighborAs: '',
  reflectorId: null,
  localPref: '',
  tag: null,
  family: 'all'
};

export const FILTER_FIELD_KEYS: Array<keyof RouteFilters> = [
  'search',
  'prefix',
  'neighborIp',
  'neighborAs',
  'reflectorId',
  'localPref',
  'tag',
  'family'
];

export function isFilterActive(filters: RouteFilters, key: keyof RouteFilters): boolean {
  const value = filters[key];
  if (key === 'family') {
    return value !== 'all';
  }
  // Both use null-for-inactive, because an empty string is itself a meaningful value for them.
  if (key === 'reflectorId' || key === 'tag') {
    return value !== null;
  }
  return typeof value === 'string' && value.trim().length > 0;
}

export function countActiveFilters(filters: RouteFilters): number {
  return FILTER_FIELD_KEYS.reduce((count, key) => count + (isFilterActive(filters, key) ? 1 : 0), 0);
}

export function clearFilterField(filters: RouteFilters, key: keyof RouteFilters): RouteFilters {
  return { ...filters, [key]: DEFAULT_ROUTE_FILTERS[key] };
}

/**
 * The filters `GET /v2/reflector/visibility/routes` can apply itself, as wire query params.
 *
 * `tag` and `search` are NOT sent. The endpoint has no tag filter at all, and its
 * `search` haystack is narrower than the one below — it omits family, subnet, RD/RT,
 * origin, status, decision reason, the withdrawn-by peer and the tag text. Sending
 * either would silently drop rows the same query keeps today, so both are applied
 * here, over whatever the server already narrowed. The split is stated once, here,
 * so the two sides cannot drift into filtering the same query differently.
 *
 * Inactive filters are omitted entirely rather than sent blank, since the endpoint
 * treats a present-but-empty param as a no-op only by accident of its own coercion.
 */
export function toServerRouteFilters(
  filters: RouteFilters,
  bestPathOnly: boolean
): Record<string, string> {
  const params: Record<string, string> = {};
  const put = (key: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      params[key] = trimmed;
    }
  };

  put('prefix', filters.prefix);
  put('neighborIp', filters.neighborIp);
  put('neighborAs', filters.neighborAs);
  put('localPref', filters.localPref);
  if (filters.reflectorId) {
    params.reflectorId = filters.reflectorId;
  }
  if (filters.family !== 'all') {
    params.family = filters.family;
  }
  if (bestPathOnly) {
    params.bestOnly = 'true';
  }

  return params;
}

/**
 * Applies only the two filters the server cannot (`tag`, `search`), for rows
 * that already came back filtered by everything else. Passing server-filtered rows
 * through the full `applyRouteFilters` would work too — the filters are idempotent —
 * but would hide which side is actually responsible for each one.
 */
export function applyClientOnlyRouteFilters(rows: RouteRow[], filters: RouteFilters): RouteRow[] {
  // Everything else is left at its inactive default: it was already applied server-side,
  // including `bestPathOnly`, which is why false is passed here rather than the toggle.
  return applyRouteFilters(
    rows,
    { ...DEFAULT_ROUTE_FILTERS, tag: filters.tag, search: filters.search },
    false
  );
}

/**
 * Matches a row's discovery tag against a `tag =` query.
 *
 * `RT` matches on the key alone; `RT:105` (or `RT=105`) requires both sides. Both are
 * case-insensitive and partial, so `rt:105` finds `RT : 105:2` — consistent with every other
 * text filter here. A destination with several tags matches on any one of them. A row with no
 * tag never matches, so filtering by tag inherently narrows to tag-discovered destinations.
 */
function matchesTagQuery(row: RouteRow, query: string): boolean {
  const tags = row.tagAttribution?.tags ?? [];
  if (tags.length === 0) {
    return false;
  }
  // Presence-only (`tag` with no value): every tag-discovered destination qualifies.
  if (!query) {
    return true;
  }

  // Split on the FIRST `:` or `=`, so the query mirrors how tags are displayed (`RT : 105:2`)
  // while still accepting `RT=105`. First-separator-wins is what keeps a tag value that itself
  // contains a colon — which real payloads do — matchable as one unit.
  const separator = query.search(/[:=]/);
  const namePart = separator === -1 ? '' : query.slice(0, separator).trim();
  const valuePart = separator === -1 ? '' : query.slice(separator + 1).trim();

  // A destination can carry several tags; matching ANY of them keeps it in view.
  return tags.some(tag => {
    const name = tag.name.toLowerCase();
    const value = tag.value.toLowerCase();
    if (separator === -1) {
      return name.includes(query) || value.includes(query);
    }
    return (!namePart || name.includes(namePart)) && (!valuePart || value.includes(valuePart));
  });
}

/**
 * Applies the shared filter set to a row list. `bestPathOnly` is passed separately
 * since it's driven by the existing BestOnlyToggle control rather than this model.
 */
export function applyRouteFilters(rows: RouteRow[], filters: RouteFilters, bestPathOnly: boolean): RouteRow[] {
  const searchQuery = filters.search.trim().toLowerCase();
  const prefixQuery = filters.prefix.trim().toLowerCase();
  const neighborIpQuery = filters.neighborIp.trim().toLowerCase();
  const neighborAsQuery = filters.neighborAs.trim();
  const neighborAsNum = neighborAsQuery === '' ? null : Number(neighborAsQuery);
  const localPrefQuery = filters.localPref.trim();
  const localPrefNum = localPrefQuery === '' ? null : Number(localPrefQuery);
  const tagQuery = filters.tag === null ? null : filters.tag.trim().toLowerCase();

  return rows.filter(row => {
    if (bestPathOnly && !row.isBestPath) {
      return false;
    }
    if (searchQuery) {
      // The haystack spans both the columns and the fields surfaced in the expandable detail
      // row (VPC, route table / subnet, RD/RT, origin, decision reason, withdrawn-by), so a
      // global search matches on anything the user can see once a group is expanded.
      const haystack = [
        row.prefix,
        row.neighborIp,
        row.asPath.join(' '),
        row.nextHop || '',
        row.reflectorName,
        row.neighborAs != null ? String(row.neighborAs) : '',
        row.family,
        row.vpc || '',
        row.routeTableId || '',
        row.subnetId || '',
        row.rd || '',
        row.rt || '',
        row.origin || '',
        row.status || '',
        row.decisionReason || '',
        row.withdrawnNeighborIp || '',
        (row.tagAttribution?.tags ?? []).map(tag => `${tag.name} ${tag.value}`).join(' '),
        row.withdrawn ? 'withdrawn withdraw' : ''
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(searchQuery)) {
        return false;
      }
    }
    if (prefixQuery && !row.prefix.toLowerCase().includes(prefixQuery)) {
      return false;
    }
    if (neighborIpQuery && !row.neighborIp.toLowerCase().includes(neighborIpQuery)) {
      return false;
    }
    if (neighborAsNum !== null && !Number.isNaN(neighborAsNum) && row.neighborAs !== neighborAsNum) {
      return false;
    }
    if (filters.reflectorId && row.reflectorId !== filters.reflectorId) {
      return false;
    }
    if (localPrefNum !== null && !Number.isNaN(localPrefNum) && row.localPref !== localPrefNum) {
      return false;
    }
    if (tagQuery !== null && !matchesTagQuery(row, tagQuery)) {
      return false;
    }
    if (filters.family !== 'all' && row.family !== filters.family) {
      return false;
    }
    return true;
  });
}
